import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, ShieldCheck, Sparkles, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOT = { green: 'bg-green-500', yellow: 'bg-amber-500', red: 'bg-red-500' };
const RING = { green: 'text-green-600', yellow: 'text-amber-600', red: 'text-red-600' };

const AdminTabSeoHealth = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/admin/seo/health/latest`);
      setReport(r.data && r.data.score !== null ? r.data : null);
    } catch {
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runCheck = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API_URL}/admin/seo/health/run`);
      setReport(r.data);
      toast.success(`SEO check complete — score ${r.data.score}/100`);
    } catch {
      toast.error('Failed to run SEO check');
    } finally {
      setRunning(false);
    }
  };

  const warmPages = async () => {
    setBusy('warm');
    try {
      const r = await axios.post(`${API_URL}/admin/community-resources/seo/generate?limit=561`);
      toast.success(r.data.status === 'already_running' ? 'Generation already running…' : 'Started generating Community Resource pages (background)');
    } catch {
      toast.error('Failed to start page generation');
    } finally {
      setBusy('');
    }
  };

  const reaudit = async () => {
    setBusy('reaudit');
    try {
      const r = await axios.post(`${API_URL}/admin/marketplace/reaudit-internships`);
      toast.success(`Re-audit done — kept ${r.data.kept_active}, quarantined ${r.data.quarantined}, removed ${r.data.removed}`);
      runCheck();
    } catch {
      toast.error('Failed to re-audit internships');
    } finally {
      setBusy('');
    }
  };

  if (loading) {
    return <div className="py-16 flex justify-center" data-testid="seo-health-loading"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>;
  }

  const lastAt = report?.generated_at ? new Date(report.generated_at).toLocaleString() : null;

  return (
    <div className="space-y-6" data-testid="admin-seo-health">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl font-bold text-gray-900">SEO Health</h2>
          <p className="text-sm text-gray-500">Automated check runs daily at 8:30 AM Central, after the morning import. {lastAt && <>Last check: {lastAt}</>}</p>
        </div>
        <Button onClick={runCheck} disabled={running} data-testid="seo-run-check">
          {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Run check now
        </Button>
      </div>

      {!report ? (
        <div className="bg-white rounded-xl p-8 border border-gray-100 text-center text-gray-500">
          No SEO check has run yet. Click <strong>Run check now</strong>.
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Score */}
          <div className="bg-white rounded-xl p-6 border border-gray-100 flex flex-col items-center justify-center" data-testid="seo-score-card">
            <div className={`text-6xl font-bold ${RING[report.overall] || 'text-gray-700'}`}>{report.score}</div>
            <div className="text-sm text-gray-500 mt-1">out of 100</div>
            <div className={`mt-3 text-xs font-semibold uppercase tracking-wide px-3 py-1 rounded-full ${report.overall === 'green' ? 'bg-green-50 text-green-700' : report.overall === 'yellow' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>
              {report.overall === 'green' ? 'Healthy' : report.overall === 'yellow' ? 'Needs review' : 'Action needed'}
            </div>
          </div>

          {/* Checks */}
          <div className="lg:col-span-2 bg-white rounded-xl p-6 border border-gray-100" data-testid="seo-checks">
            <div className="space-y-3">
              {report.checks.map((c) => (
                <div key={c.key} className="flex items-start gap-3 py-2 border-b border-gray-50 last:border-0" data-testid={`seo-check-${c.key}`}>
                  <span className={`mt-1.5 w-2.5 h-2.5 rounded-full flex-shrink-0 ${DOT[c.status] || 'bg-gray-300'}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-medium text-gray-900">{c.label}</span>
                      <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">{String(c.value)}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{c.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="bg-white rounded-xl p-6 border border-gray-100">
        <h3 className="font-semibold text-gray-900 mb-3">Quick actions</h3>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={warmPages} disabled={busy === 'warm'} data-testid="seo-warm-pages">
            {busy === 'warm' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Generate Community Resource pages
          </Button>
          <Button variant="outline" onClick={reaudit} disabled={busy === 'reaudit'} data-testid="seo-reaudit">
            {busy === 'reaudit' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Filter className="w-4 h-4 mr-2" />}
            Re-audit internships
          </Button>
          <a href={`${API_URL}/sitemap.xml`} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" data-testid="seo-view-sitemap"><ShieldCheck className="w-4 h-4 mr-2" />View sitemap.xml</Button>
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminTabSeoHealth;
